/**
 * Image URL helpers — performance only, no visual change.
 *
 * Supabase Storage supports on-the-fly transforms via /render/image/public/...
 * which also negotiates WebP/AVIF automatically based on the Accept header.
 * Supabase images use its native transformer. CDN assets and legacy external
 * images use weserv.nl, which negotiates AVIF/WebP and caches variants for a year.
 */

const SUPABASE_PUBLIC = "/storage/v1/object/public/";
const SUPABASE_RENDER = "/storage/v1/render/image/public/";
const IMAGE_PROXY = "https://images.weserv.nl/";

export function isTransformable(url?: string | null): boolean {
  if (!url || /^(data:|blob:)/i.test(url)) return false;
  return !/\.svg(?:[?#]|$)/i.test(url);
}

function proxySource(url: string): string {
  if (/^https?:\/\//i.test(url)) return url.replace(/^https?:\/\//i, "");
  return `blog.logcomex.ai/${url.replace(/^\//, "")}`;
}

/** Returns a width-constrained, quality-optimized variant when possible. */
export function imgUrl(url?: string | null, width = 800, quality = 90): string {
  if (!url) return "";
  if (!isTransformable(url)) return url;
  if (url.includes(SUPABASE_PUBLIC) && url.includes(".supabase.co")) {
    const base = url.replace(SUPABASE_PUBLIC, SUPABASE_RENDER);
    const sep = base.includes("?") ? "&" : "?";
    return `${base}${sep}width=${width}&quality=${quality}&resize=contain`;
  }
  const params = new URLSearchParams({
    url: proxySource(url),
    w: String(width),
    q: String(Math.max(1, Math.min(100, quality))),
    output: "webp",
    // Never enlarge a small original: that creates pixels, not detail.
    we: "",
    // Edge cache the derived variant for a year (weserv CDN, ~global PoPs).
    maxage: "1y",
    il: "",

  });
  return `${IMAGE_PROXY}?${params.toString()}`;
}

/** Builds a srcset across common breakpoints. Empty string when not supported. */
export function imgSrcSet(url?: string | null, widths: number[] = [400, 800, 1200, 1920]): string {
  if (!isTransformable(url)) return "";
  return widths.map((w) => `${imgUrl(url, w)} ${w}w`).join(", ");
}

/** Adds responsive delivery attributes to images embedded in trusted rich text. */
export function optimizeHtmlImages(html: string, maxWidth = 720): string {
  if (!html || typeof DOMParser === "undefined") return html;
  const doc = new DOMParser().parseFromString(`<div id="image-root">${html}</div>`, "text/html");
  const root = doc.getElementById("image-root");
  if (!root) return html;
  root.querySelectorAll("img").forEach((image) => {
    const source = image.getAttribute("src");
    if (!source) return;
    image.setAttribute("src", imgUrl(source, maxWidth));
    const srcset = imgSrcSet(source, [360, 720, 1080, 1440]);
    if (srcset) image.setAttribute("srcset", srcset);
    image.setAttribute("sizes", "(max-width: 768px) calc(100vw - 32px), 720px");
    image.setAttribute("loading", "lazy");
    image.setAttribute("decoding", "async");
  });
  return root.innerHTML;
}
